import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, CalendarCheck, Phone, ArrowRight } from "lucide-react";
import { CONTACT, IMG } from "@/data/site";
import { MainSlide } from "./HeroBanner";

const SLIDES = [
  { id: "main", tab: "दोनों विभाग", tabEn: "Eye + Surgery", bg: IMG.reception, pos: "object-right", tone: "from-maroon-ink via-maroon-ink/90 to-maroon-ink/40" },
  {
    id: "eye", tab: "नेत्र सेवाएँ", tabEn: "Eye Care", bg: IMG.inauguration, pos: "object-[center_30%]", tone: "from-maroon-ink via-maroon-ink/85 to-transparent",
    tag: "Dr. Rakhshanda · MS (AMU), ex-LVPEI", title: "बिना टाँका, बिना दर्द — मोतियाबिंद का आधुनिक ऑपरेशन",
    sub: "Topical phaco cataract surgery, glaucoma, retina & complete eye check-ups.", cta: { to: "/eye-care", label: "Eye Care सेवाएँ देखें" }, btn: "btn-maroon",
  },
  {
    id: "surgery", tab: "दूरबीन सर्जरी", tabEn: "Surgery", bg: IMG.surgeryOT, pos: "object-center", tone: "from-surg-deep via-surg-deep/85 to-transparent",
    tag: "Dr. Danish · MS (DNB), Laparoscopic Surgeon", title: "छोटा चीरा, कम दर्द — दूरबीन से ऑपरेशन",
    sub: "Hernia, gallbladder stone, appendix, piles, fistula & more — faster recovery.", cta: { to: "/surgery", label: "Surgery सेवाएँ देखें" }, btn: "btn-teal",
  },
  {
    id: "free-checkup", tab: "नि:शुल्क जाँच", tabEn: "Every Thursday", bg: IMG.exam, pos: "object-[center_35%]", tone: "from-maroon-ink via-maroon-ink/80 to-transparent",
    tag: "हर बृहस्पतिवार · Every Thursday", title: "नि:शुल्क आँखों की जाँच — सबके लिए",
    sub: "Free computerized eye check-up: vision, spectacle power and eye pressure.", cta: { to: "/#appointment", label: "अपना नाम दर्ज करें" }, btn: "btn-maroon",
  },
  {
    id: "optical", tab: "ऑप्टिकल व फ़ार्मेसी", tabEn: "Optical", bg: IMG.pharmacy, pos: "object-center", tone: "from-maroon-ink via-maroon-ink/80 to-transparent",
    tag: "एक ही छत के नीचे · Under one roof", title: "चश्मे, दवाइयाँ और देखभाल — अस्पताल में ही",
    sub: "In-house optical store and pharmacy for your prescriptions and spectacles.", cta: { to: "/contact", label: "हमसे मिलें" }, btn: "btn-maroon",
  },
];

const SimpleSlide = ({ s }) => (
  <div className="container-x relative flex min-h-[560px] items-center py-16 md:min-h-[600px]">
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .7, delay: .15 }} className="max-w-2xl">
      <p className="eyebrow inline-block rounded-full bg-white/15 px-3 py-1.5 text-amber-300 backdrop-blur">{s.tag}</p>
      <h2 className="font-display mt-5 text-4xl leading-[1.15] text-white sm:text-5xl lg:text-6xl">{s.title}</h2>
      <p className="font-en mt-5 text-base text-white/80 md:text-lg">{s.sub}</p>
      <div className="mt-8 flex flex-wrap gap-3">
        <Link to={s.cta.to} data-testid={`hero-slide-cta-${s.id}`} className="btn btn-light">{s.cta.label} <ArrowRight className="h-4 w-4" /></Link>
        <Link to="/#appointment" className={`btn ${s.btn}`}><CalendarCheck className="h-4 w-4" /> अपॉइंटमेंट</Link>
        <a href={`tel:${CONTACT.phoneDial}`} className="btn btn-outline text-white"><Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}</a>
      </div>
    </motion.div>
  </div>
);

const Controls = ({ i, go, paused }) => (
  <div className="container-x absolute inset-x-0 bottom-5 z-10 flex items-center gap-3">
    <div className="flex flex-1 justify-center gap-2 md:hidden">
      {SLIDES.map((s, k) => (
        <button key={s.id} onClick={() => go(k)} data-testid={`hero-dot-${s.id}`} aria-label={s.tabEn}
          className={`h-2.5 rounded-full transition-all ${k === i ? "w-8 bg-amber-300" : "w-2.5 bg-white/40"}`} />
      ))}
    </div>
    <div className="hidden flex-1 gap-2 md:flex">
      {SLIDES.map((s, k) => (
        <button key={s.id} onClick={() => go(k)} data-testid={`hero-tab-${s.id}`} aria-label={s.tabEn}
          className={`relative min-w-[120px] flex-1 overflow-hidden rounded-xl border px-3 py-2 text-left transition-colors ${k === i ? "border-white/50 bg-white/20" : "border-white/15 bg-white/5 hover:bg-white/10"}`}>
          <span className="font-en block text-[10px] text-white/60">0{k + 1} · {s.tabEn}</span>
          <span className="block truncate text-sm font-semibold text-white">{s.tab}</span>
          {k === i && !paused && <motion.span key={`p${i}`} initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ duration: 6, ease: "linear" }} className="absolute bottom-0 left-0 h-0.5 bg-amber-300" />}
        </button>
      ))}
    </div>
    <button onClick={() => go(i - 1)} data-testid="hero-prev" aria-label="Previous" className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/30 text-white hover:bg-white/15 md:flex"><ChevronLeft className="h-5 w-5" /></button>
    <button onClick={() => go(i + 1)} data-testid="hero-next" aria-label="Next" className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/30 text-white hover:bg-white/15 md:flex"><ChevronRight className="h-5 w-5" /></button>
  </div>
);

export const HeroSlider = () => {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const go = useCallback((k) => setI((k + SLIDES.length) % SLIDES.length), []);
  useEffect(() => {
    if (paused) return undefined;
    const t = setTimeout(() => go(i + 1), 6000);
    return () => clearTimeout(t);
  }, [i, paused, go]);
  const s = SLIDES[i];

  return (
    <section data-testid="hero-banner" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} className="grain relative overflow-hidden bg-maroon-ink pb-16 md:pb-24">
      <AnimatePresence mode="wait">
        <motion.div key={s.id} data-testid={`hero-slide-${s.id}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: .6 }}>
          <img src={s.bg} alt="" className={`kenburns absolute inset-0 h-full w-full object-cover ${s.pos} ${s.id === "main" ? "opacity-35" : "opacity-90"}`} />
          <div className={`absolute inset-0 bg-gradient-to-r ${s.tone}`} />
          {s.id === "main" ? <MainSlide /> : <SimpleSlide s={s} />}
        </motion.div>
      </AnimatePresence>
      <Controls i={i} go={go} paused={paused} />
    </section>
  );
};
